import React from 'react';
import { IconsList } from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTableHeader';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import ProductCell from 'src/components/BeaconV2Components/ProductCell/ProductCell';
import AtlasV2ProductListContainer from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2ProductListContainer';
import { usePaginatedProductData } from 'src/components/AtlasV2Components/utils/tableUtils';
import {
  DorisGroupByFieldNameEnum,
  DorisMetricEndPointEnum,
  DorisMetricTypeEnum
} from 'src/components/AtlasV2Components/Consts/DorisFieldNameEnum';
import AtlasV2MetricListContainer from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2MetricListContainer';
import { getWeekColumnDefinition } from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import { useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import { removeComparisonFormatter } from 'src/components/BeaconV2Components/MetricList/utils';
import AtlasSalesExportModal from 'src/components/AtlasV2Components/AtlasDorisExportComponents/AtlasSalesExportModal';
import { getAtlasSalesV2Treatment } from 'src/utils/app';

const fields = [
  {
    name: 'retailSales',
    displayName: 'Retail Sales',
    wrapperStyle: {
      width: '93px'
    }
  },
  {
    name: 'retailPrice',
    displayName: 'Retail Price',
    wrapperStyle: {
      width: '93px'
    }
  },
  {
    name: 'unitsSold',
    displayName: 'Units Sold',
    wrapperStyle: {
      width: '93px'
    }
  }
];

export interface ProductMetadata {
  stacklineSku: string;
  title: string;
  retailerSku: string;
  imageUrl?: string;
  [key: string]: any;
}

const tableTitleMap = {
  retailSales: 'Retail Sales',
  retailPrice: 'Retail Price',
  unitsSold: 'Units Sold'
};

const salesSortByFieldNameMap = {
  retailSales: DorisMetricTypeEnum.RetailSales,
  retailPrice: DorisMetricTypeEnum.RetailSales,
  unitsSold: DorisMetricTypeEnum.UnitsSold
};

const mainFieldNameMap = {
  retailSales: DorisMetricTypeEnum.RetailSales,
  retailPrice: DorisMetricTypeEnum.RetailPrice,
  unitsSold: DorisMetricTypeEnum.UnitsSold
};

export const AtlasSalesV2ProductGrid = ({ salesType = 'retailSales' }: { salesType: string }) => {
  const { isProductEntity } = useProductEntity();
  // Use the new custom hook instead of managing all the state manually
  const {
    enrichedData,
    isLoading,
    isMetadataLoading,
    currentPage,
    handleNextPage,
    handlePreviousPage,
    getPagedValuesByFieldName,
    getPagedValueDifferencesByFieldName
  } = usePaginatedProductData({
    apiMethodName: DorisMetricEndPointEnum.SalesEndPoint,
    groupByFieldName: isProductEntity ? DorisGroupByFieldNameEnum.Week : DorisGroupByFieldNameEnum.Product,
    metricType: DorisMetricTypeEnum.ProductsWithSalesData,
    additionalParams: {
      sortby: salesSortByFieldNameMap[salesType],
      ...(isProductEntity ? { skipComparison: true } : {})
    },
    fields
  });

  return (
    <Flex flexDirection="column" gap={16}>
      {isProductEntity ? (
        <AtlasV2MetricListContainer
          entityTableHeaderProps={{
            title: 'Retail Sales by Week',
            customExportModal: (props: { open: boolean; close: () => void }) => (
              <AtlasSalesExportModal
                open={props.open}
                onClose={props.close}
                metricType={salesType as DorisMetricTypeEnum}
              />
            )
          }}
          indexName="sales"
          sortFieldName="retailSales"
          fields={fields.map(removeComparisonFormatter)}
          customDataSource={{
            data: enrichedData,
            isLoading: isLoading,
            getValuesByFieldName: getPagedValuesByFieldName,
            getValueDifferencesByFieldName: getPagedValueDifferencesByFieldName
          }}
          headColumnDefinition={getWeekColumnDefinition({
            getTableColumnProps(defaults) {
              return {
                ...defaults,
                style: {
                  ...defaults.style,
                  width: '220px'
                }
              };
            }
          })}
          shouldHidePagination
          postProcessData={(data) => {
            return data.sort((a, b) => b.weekId - a.weekId);
          }}
          useTableDataProps={{
            groupByFieldName: 'weekId',
            filterResponse: () => true,
            itemsPerPage: 100
          }}
          showGrid={false}
        />
      ) : (
        <AtlasV2ProductListContainer
          showGrid
          indexName="sales"
          sortFieldName={salesSortByFieldNameMap[salesType]}
          mainFieldName={mainFieldNameMap[salesType]}
          fields={fields}
          entityTableHeaderProps={{
            title: `${tableTitleMap[salesType]} by Product`,
            customExportModal: (props: { open: boolean; close: () => void }) => (
              <AtlasSalesExportModal
                open={props.open}
                onClose={props.close}
                metricType={salesType as DorisMetricTypeEnum}
              />
            )
          }}
          headColumnDefinition={{
            headerName: 'Product',
            valueFormatter: (_, row: { product: { id: string; title: string } }) => {
              return (
                <div className="product-col" style={{ width: '580px' }}>
                  <ProductCell stacklineSku={row.product?.id} title={row.product?.title} />
                </div>
              );
            }
          }}
          customDataSource={{
            data: enrichedData,
            isLoading: isLoading || isMetadataLoading,
            getValuesByFieldName: getPagedValuesByFieldName,
            getValueDifferencesByFieldName: getPagedValueDifferencesByFieldName
          }}
          useTableDataProps={{
            itemsPerPage: 300
          }}
          shouldHidePagination
          iconList={[IconsList.export, IconsList.tile, IconsList.table]}
        />
      )}
      <Flex justifyContent="flex-end">
        <Pagination
          currentPage={currentPage}
          shouldUseInfinitePagination
          onClickNext={handleNextPage}
          capPage={50}
          onClickPrevious={handlePreviousPage}
        />
      </Flex>
    </Flex>
  );
};
